import { Stack, Skeleton, Button, useDisclosure } from '@chakra-ui/react';
import Content from 'src/components/Content';
import SectionPanel from '@/components/SectionPanel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TrelloBoard from '@/pages/boards/TrelloBoard';
import { boardService } from 'src/service/BoardService';
import { FcDocument } from 'react-icons/fc';
import { BiTrash, BiColumns } from 'react-icons/bi';
import CardDialog from './CardDialog';
import CardComponent from './CardComponent';
import { BoardCard } from 'src/api/BoardCard';
import { Board as BoardData } from 'src/api/Board';
import LaneHeaderComponent from './LaneHeaderComponent';
import type { DataTableCellContext as CellContext, DataTableColumnDef as ColumnDef } from 'src/components/DataTable';
import DataTable from 'src/components/DataTable';
import { BoardTag } from 'src/api/BoardTag';
import { IdAndName } from '@/api/IdAndName';
import Tags from './Tags';
import ConfirmDialog from 'src/components/ConfirmDialog';
import WaitingSpinner from '@/components/WaitingSpinner';
import { formatBoardDueLabel, formatBoardTimestamp } from '@/data/DateTimeUtil';
import { Tooltip } from '@/components/ui/tooltip';
import { useAuthContext } from '@/auth/AuthContext';
import { boardUserName } from './BoardUsers';

type CardPosition = {
  laneId: string;
  index: number;
};

const cardPositions = (board?: BoardData | null): Map<string, CardPosition> =>
  new Map(
    board?.lanes.flatMap(lane =>
      lane.cards.map((card, index) => [card.id, { laneId: lane.id, index }] as [string, CardPosition])
    ) ?? []
  );

const cardsById = (board?: BoardData | null): Map<string, BoardCard> =>
  new Map(board?.lanes.flatMap(lane => lane.cards.map(card => [card.id, card] as [string, BoardCard])) ?? []);

const comparableCard = (card: BoardCard): string =>
  JSON.stringify({
    title: card.title,
    description: card.description ?? null,
    label: card.label ?? null,
    tags: card.tags ?? [],
    assignedTo: card.assignedTo ?? null,
  });

const normalizeCardMetadata = (
  card: BoardCard,
  previous: BoardCard | undefined,
  previousPosition: CardPosition | undefined,
  position: CardPosition,
  currentUserId: number | null,
  now: Date
): BoardCard => {
  const touched = !previous || (previousPosition && (previousPosition.laneId !== position.laneId || previousPosition.index !== position.index)) || comparableCard(previous) !== comparableCard(card);

  if (touched) {
    return {
      ...card,
      tags: card.tags ?? [],
      createdBy: previous?.createdBy ?? card.createdBy ?? currentUserId ?? null,
      createdAt: previous?.createdAt ?? card.createdAt ?? now,
      assignedTo: card.assignedTo ?? null,
      updatedAt: now,
    };
  }

  return {
    ...card,
    tags: card.tags ?? [],
    createdBy: previous?.createdBy ?? card.createdBy ?? null,
    createdAt: previous?.createdAt ?? card.createdAt ?? null,
    assignedTo: card.assignedTo ?? null,
    updatedAt: previous?.updatedAt ?? card.updatedAt ?? null,
  };
};

const normalizeBoardMetadata = (board: BoardData, previous: BoardData | null, currentUserId: number | null, now: Date): BoardData => {
  const previousCards = cardsById(previous);
  const previousPositions = cardPositions(previous);
  return {
    ...board,
    lanes: board.lanes.map(lane => ({
      ...lane,
      cards: lane.cards.map((card, index) =>
        normalizeCardMetadata(card, previousCards.get(card.id), previousPositions.get(card.id), { laneId: lane.id, index }, currentUserId, now)
      ),
    })),
  };
};

const touchCardMetadata = (card: BoardCard, previous: BoardCard | undefined, currentUserId: number | null, now: Date = new Date()): BoardCard => ({
  ...card,
  tags: card.tags ?? [],
  createdBy: previous?.createdBy ?? card.createdBy ?? currentUserId ?? null,
  createdAt: previous?.createdAt ?? card.createdAt ?? now,
  assignedTo: card.assignedTo ?? null,
  updatedAt: now,
});

const Dev = () => {
  const { user } = useAuthContext();
  const currentUserId = user?.data?.id ?? null;
  const organizationId = user?.data?.organizationId;
  const [data, setData] = useState<BoardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [eventBus, setEventBus] = useState(undefined);
  const [changeCard, setChangeCard] = useState<BoardCard | null>(null);
  const [changeLaneId, setChangeLaneId] = useState<string | null>(null);
  const [deleteCallback, setDeleteCallback] = useState<(() => Promise<void>) | undefined>(undefined);
  const [backlog, setBacklog] = useState<BoardCard[]>([]);
  const [archive, setArchive] = useState<BoardCard[]>([]);
  const [users, setUsers] = useState<IdAndName[]>([]);
  const [deleteHeading, setDeleteHeading] = useState('Task Archivieren?');

  const { open: isTaskOpen, onOpen: onTaskOpen, onClose: onTaskClose } = useDisclosure();
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const removeNull = (card: BoardCard) => {
    if (card.description === null) delete card.description;
    if (card.label === null) delete card.label;
    return card;
  }

  const onBoardUpdated = useCallback((next: BoardData) => {
    const rawBoard: BoardData = {
      ...next,
      name: next.name ?? 'dev',
    };
    const board = normalizeBoardMetadata(rawBoard, data, currentUserId, new Date());
    console.log('board dev updating...');
    boardService.update(board).catch(err => console.error('Failed to update dev board', err));
    setData(board);
    console.log('board dev has been updated');
  }, [currentUserId, data]);

  // new or update task, callback from entry dialog
  const editedCard = (aCard: BoardCard) => {
    let type = changeCard === null ? 'ADD_CARD' : 'UPDATE_CARD';
    let laneId = changeLaneId === null ? 'todo' : changeLaneId;
    const card = removeNull(touchCardMetadata(aCard, changeCard ?? undefined, currentUserId));
    console.log(`card[${type}] = ${JSON.stringify(card)}`);
    if (type === 'ADD_CARD') {
      // instead of add to bottom, make a lane update to put the new card in the front
      const cards = data.lanes.find(l => l.id === laneId)?.cards.concat([]) ?? [];
      cards.unshift(card);
      eventBus.publish({
        type: 'UPDATE_CARDS',
        laneId: laneId,
        cards: cards
      });
    } else {
      eventBus.publish({
        type: type,
        laneId: laneId,
        card: card,
      });
    }
  }

  const findCard = (cardId: string, laneId: string): BoardCard | undefined => {
    const lane = data?.lanes.find(l => l.id === laneId);
    return lane?.cards.find(c => c.id === cardId);
  }

  const onCardClicked = (cardId, metadata, laneId) => {
    console.log(`editing ${cardId} on lane ${laneId} metadata ${metadata}`);
    let card = findCard(cardId, laneId);
    if (!card) return;
    setChangeCard({ ...card });
    setChangeLaneId(laneId);
    console.log(`found card ${card.title} on lane ${laneId}`);
    onTaskOpen();
  }

  const onBeforeCardDelete = (callback) => {
    setDeleteHeading('Task Archivieren?');
    setDeleteCallback(() => async () => {
      callback();
    });
    onDeleteOpen();
  }

  const onCardDelete = async (cardId, laneId) => {
    console.log(`archiving card ${cardId}, ${laneId}`);
    const card = findCard(cardId, laneId);
    if (card) {
      console.log(`found card ${card.title}`)
      const archivedCard = touchCardMetadata(card, card, currentUserId);
      await boardService.archive(archivedCard).then(_ => setArchive(data => {
        let on = [...data];
        on.unshift(archivedCard);
        return on;
      }));
    }
  }

  useEffect(() => {
    setIsLoading(true);
    const a = boardService.findWithUsers('dev').then(res => {
      setUsers(res.users.concat());
      setData(old => {
        let m = {
          ...old,
          ...res.board
        };
        return m;
      });
    }).catch(err => {
      console.log(err);
      // set a default board
      setData({
        name: 'dev', lanes: [
          {
            id: 'todo',
            title: 'To Do 📋',
            label: '0',
            cards: []
          },
          {
            id: 'bugs',
            title: 'Analysis 🧮',
            label: '0',
            cards: []
          },
          {
            id: 'work',
            title: 'In Work ⚙️',
            label: '0',
            cards: []
          },
          {
            id: 'done',
            title: 'Done 🎉',
            label: '0',
            cards: []
          }
        ]
      })
    });
    const b = boardService.findLane('archive').then(res => {
      console.log(`archived cards ${res.length}`);
      setArchive(res);
    });
    const c = boardService.findLane('backlog').then(res => {
      console.log(`backlog cards ${res.length}`);
      setBacklog(res);
    });
    Promise.all([a, b, c]).then(_ => { }).finally(() => setIsLoading(false));
  }, []);

  const columns: ColumnDef<BoardCard>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
      },
      {
        id: 'description',
        header: 'Description',
        accessorKey: 'description',
      },
      {
        id: 'tags',
        header: 'Tags',
        accessorFn: (row: BoardCard) => row.tags,
        cell: (info: CellContext<BoardCard, BoardTag[]>) => {
          const tags = info.getValue();
          return (<Tags tags={tags} />);
        },
      },
      {
        id: 'due',
        header: 'Due',
        accessorKey: 'label',
        cell: (info: CellContext<BoardCard, string | null>) => formatBoardDueLabel(info.getValue()) ?? '',
      },
      {
        id: 'createdBy',
        header: 'Created by',
        accessorKey: 'createdBy',
        cell: (info: CellContext<BoardCard, number | null>) => boardUserName(users, info.getValue()),
      },
      {
        id: 'assignedTo',
        header: 'Assigned to',
        accessorKey: 'assignedTo',
        cell: (info: CellContext<BoardCard, number | null>) => boardUserName(users, info.getValue()),
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        cell: (info: CellContext<BoardCard, Date | null>) => formatBoardTimestamp(info.getValue()),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        cell: (info: CellContext<BoardCard, Date | null>) => formatBoardTimestamp(info.getValue()),
      },
    ], [users]
  );
  const confirmLaneDeletion = useCallback((laneId: 'backlog' | 'archive', card: BoardCard) => {
    const heading = laneId === 'backlog' ? 'Backlog Karte löschen?' : 'Archiv Karte löschen?';
    setDeleteHeading(heading);
    setDeleteCallback(() => async () => {
      await boardService.deleteFromLane(laneId, card.id);
      if (laneId === 'backlog') {
        setBacklog(items => items.filter(item => item.id !== card.id));
      } else {
        setArchive(items => items.filter(item => item.id !== card.id));
      }
    });
    onDeleteOpen();
  }, [onDeleteOpen]);

  const moveBacklogCardToArchive = useCallback(async (card: BoardCard) => {
    try {
      const movedCard = touchCardMetadata(card, card, currentUserId);
      await boardService.moveToLane('archive', card.id);
      setBacklog(items => items.filter(item => item.id !== card.id));
      setArchive(items => [movedCard, ...items]);
    } catch (error) {
      console.error(`Failed to move backlog card ${card.id} to archive`, error);
    }
  }, [currentUserId]);

  const moveArchiveCardToBacklog = useCallback(async (card: BoardCard) => {
    try {
      const movedCard = touchCardMetadata(card, card, currentUserId);
      await boardService.moveToLane('backlog', card.id);
      setArchive(items => items.filter(item => item.id !== card.id));
      setBacklog(items => [movedCard, ...items]);
    } catch (error) {
      console.error(`Failed to move archived card ${card.id} to backlog`, error);
    }
  }, [currentUserId]);

  const moveLaneCardToBoard = useCallback(async (laneId: 'backlog' | 'archive', card: BoardCard) => {
    if (!data) return;

    const targetLane = data.lanes.find(lane => lane.id === 'todo') ?? data.lanes[0];
    if (!targetLane) return;

    const cardToMove = removeNull(touchCardMetadata({ ...card, tags: [...card.tags] }, card, currentUserId));
    const nextBoard: BoardData = {
      ...data,
      name: data.name ?? 'dev',
      lanes: data.lanes.map(lane => {
        const cards = lane.cards.filter(item => item.id !== card.id);
        return lane.id === targetLane.id
          ? { ...lane, cards: [cardToMove, ...cards] }
          : { ...lane, cards };
      }),
    };

    try {
      await boardService.update(nextBoard);
      await boardService.deleteFromLane(laneId, card.id);
      setData(nextBoard);
      if (laneId === 'backlog') {
        setBacklog(items => items.filter(item => item.id !== card.id));
      } else {
        setArchive(items => items.filter(item => item.id !== card.id));
      }
    } catch (error) {
      console.error(`Failed to move ${laneId} card ${card.id} to board`, error);
    }
  }, [currentUserId, data]);

  const createColumnsWithActions = useCallback((laneId: 'backlog' | 'archive') => {
    const actionColumn: ColumnDef<BoardCard> = {
      id: `delete-${laneId}`,
      header: '',
      size: 60,
      cell: (info: CellContext<BoardCard, BoardTag[]>) => (
        <Stack direction='row' justify='flex-end' gap={1}>
          {laneId === 'backlog' && (
            <Tooltip content='Move to Archive'>
              <Button
                size='xs'
                variant='ghost'
                colorPalette='orange'
                aria-label='Move to Archive'
                className='data-table-action'
                onClick={event => {
                  event.stopPropagation();
                  moveBacklogCardToArchive(info.row.original);
                }}
              >🗄️</Button>
            </Tooltip>
          )}
          {laneId === 'archive' && (
            <Tooltip content='Move to Backlog'>
              <Button
                size='xs'
                variant='ghost'
                colorPalette='green'
                aria-label='Move to Backlog'
                className='data-table-action'
                onClick={event => {
                  event.stopPropagation();
                  moveArchiveCardToBacklog(info.row.original);
                }}
              >📋</Button>
            </Tooltip>
          )}
          <Tooltip content='Add to Board'>
            <Button
              size='xs'
              variant='ghost'
              colorPalette='blue'
              aria-label='Add to Board'
              className='data-table-action'
              onClick={event => {
                event.stopPropagation();
                moveLaneCardToBoard(laneId, info.row.original);
              }}
            >
              <BiColumns />
            </Button>
          </Tooltip>
          <Tooltip content='Delete'>
            <Button
              size='xs'
              variant='ghost'
              colorPalette='red'
              aria-label='Delete'
              className='data-table-action'
              onClick={(event) => {
                event.stopPropagation();
                confirmLaneDeletion(laneId, info.row.original);
              }}
            >
              <BiTrash />
            </Button>
          </Tooltip>
        </Stack>
      ),
      enableSorting: false,
      enableHiding: false,
    };
    return [...columns, actionColumn];
  }, [columns, confirmLaneDeletion, moveBacklogCardToArchive, moveArchiveCardToBacklog, moveLaneCardToBoard]);

  const backlogColumns = useMemo(() => createColumnsWithActions('backlog'), [createColumnsWithActions]);
  const archiveColumns = useMemo(() => createColumnsWithActions('archive'), [createColumnsWithActions]);
  const BoardCardComponent = useCallback((props) => <CardComponent {...props} users={users} organizationId={organizationId} />, [organizationId, users]);

  return (
    <Content title='Development Board' actions={<Button colorPalette='blue' onClick={() => {
        setChangeCard(null);
        setChangeLaneId(null);
        onTaskOpen();
      }}><FcDocument /> New Task</Button>}>
      <Stack gap='6'>
        <SectionPanel>
          {isLoading || !data ? (
            <Stack py={5}>
              <WaitingSpinner size='xl' />
              <Skeleton height='20px' />
              <Skeleton height='20px' />
              <Skeleton height='20px' />
            </Stack>
          ) : (
            <TrelloBoard data={data}
              onDataChange={onBoardUpdated}
              onCardClick={onCardClicked}
              onBeforeCardDelete={onBeforeCardDelete}
              onCardDelete={onCardDelete}
              eventBusHandle={setEventBus}
              components={{ Card: BoardCardComponent, LaneHeader: LaneHeaderComponent }}
              draggable
              laneDraggable={false}
              editLaneTitle={false}
              style={{ padding: '30px 20px', fontFamily: 'Verdana', background: 'url(/images/mettmenalp.webp)', backgroundSize: 'cover', backgroundColor: 'white', color: 'black' }} />
          )}
        </SectionPanel>
        <SectionPanel title='Backlog 📋'>
          <DataTable columns={backlogColumns} data={backlog}
            hideColumnsOnXs={['tags', 'description', 'createdBy', 'createdAt', 'updatedAt']}
            pageSize={10}
          />
        </SectionPanel>
        <SectionPanel title='Archive 🗄️'>
          <DataTable columns={archiveColumns} data={archive}
            hideColumnsOnXs={['tags', 'description', 'createdBy', 'createdAt', 'updatedAt']}
            pageSize={10}
          />
        </SectionPanel>
      </Stack>

      <CardDialog
        data={changeCard}
        users={users}
        currentUserId={currentUserId}
        isOpen={isTaskOpen}
        onClose={onTaskClose}
        onConfirm={(card: BoardCard) => Promise.resolve(editedCard(card))} />
      <ConfirmDialog
        heading={deleteHeading}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={async (): Promise<void> => {
          const removeFn = deleteCallback;
          if (removeFn) {
            await removeFn();
            setDeleteCallback(undefined);
          }
        }}
      />
    </Content>
  );
}

export default Dev
